// import Firestore
import { collection, doc, addDoc, setDoc, deleteDoc, getDoc, getDocs, query, where, orderBy, limit, serverTimestamp, startAfter } from 'firebase/firestore';
import { db } from '../firebase';
import { createAdminNotificationMeta } from './adminNotificationMeta';

// Collections
const eventsCol = collection(db, 'events');

// Types (JSDoc for clarity)
/**
 * @typedef {Object} EventItem
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} location
 * @property {boolean} isOnline
 * @property {string | null} registrationUrl
 * @property {string | null} bannerImageUrl
 * @property {number | null} capacity
 * @property {Array<string>} tags
 * @property {import('firebase/firestore').Timestamp} startAt
 * @property {import('firebase/firestore').Timestamp} endAt
 * @property {import('firebase/firestore').Timestamp} createdAt
 * @property {import('firebase/firestore').Timestamp} updatedAt
 */

export async function createEvent(eventData) {
	const now = serverTimestamp();
	// Ensure required fields exist
	const payload = {
		title: eventData.title,
		description: eventData.description || '',
		location: eventData.location || '',
		isOnline: Boolean(eventData.isOnline),
		registrationUrl: eventData.registrationUrl || null,
		bannerImageUrl: eventData.bannerImageUrl || null,
		capacity: eventData.capacity ?? null,
		tags: Array.isArray(eventData.tags) ? eventData.tags : [],
		startAt: eventData.startAt,
		endAt: eventData.endAt,
		createdAt: now,
		updatedAt: now
	};
	const ref = await addDoc(eventsCol, payload);
	return { id: ref.id, ...payload };
}

export async function updateEvent(eventId, updates) {
	const now = serverTimestamp();
	const ref = doc(db, 'events', eventId);
	await setDoc(ref, { ...updates, updatedAt: now }, { merge: true });
	const snap = await getDoc(ref);
	return { id: ref.id, ...snap.data() };
}

export async function deleteEvent(eventId) {
	await deleteDoc(doc(db, 'events', eventId));
}

export async function getEventById(eventId) {
	const ref = doc(db, 'events', eventId);
	const snap = await getDoc(ref);
	if (!snap.exists()) return null;
	return { id: snap.id, ...snap.data() };
}

export async function listUpcomingEvents({ max = 6 } = {}) {
	const now = new Date();
	const q = query(
		eventsCol,
		where('endAt', '>=', now),
		orderBy('endAt', 'asc'),
		limit(max)
	);
	const res = await getDocs(q);
	return res.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function listPastEvents({ max = 6 } = {}) {
	const now = new Date();
	const q = query(
		eventsCol,
		where('endAt', '<', now),
		orderBy('endAt', 'desc'),
		limit(max)
	);
	const res = await getDocs(q);
	return res.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Pagination helpers
export async function listUpcomingEventsPage({ pageSize = 10, cursor } = {}) {
    const now = new Date();
    const base = [eventsCol, where('endAt', '>=', now), orderBy('endAt', 'asc'), limit(pageSize)];
    const qy = cursor ? query(...base, startAfter(cursor)) : query(...base);
    const snap = await getDocs(qy);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
    return { items, nextCursor };
}

export async function listPastEventsPage({ pageSize = 10, cursor } = {}) {
    const now = new Date();
    const base = [eventsCol, where('endAt', '<', now), orderBy('endAt', 'desc'), limit(pageSize)];
    const qy = cursor ? query(...base, startAfter(cursor)) : query(...base);
    const snap = await getDocs(qy);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
    return { items, nextCursor };
}

// Registrations (subcollection under each event)
export async function registerForEvent(eventId, registration, eventTitle = 'event') {
	const registrationsCol = collection(db, 'events', eventId, 'registrations');
	const payload = {
		name: registration.name,
		email: registration.email,
		phone: registration.phone || '',
		notes: registration.notes || '',
		createdAt: serverTimestamp(),
		eventTitle,
		...createAdminNotificationMeta({
			type: 'event_registration',
			summary: `New event registration for ${eventTitle} from ${registration.name}`,
		}),
	};
	const ref = await addDoc(registrationsCol, payload);
	return { id: ref.id, ...payload };
}

export async function listEventRegistrations(eventId) {
	const registrationsCol = collection(db, 'events', eventId, 'registrations');
	const res = await getDocs(registrationsCol);
	return res.docs.map(d => ({ id: d.id, ...d.data() }));
}
